// Flattens the 4-level nested tree (Process Step -> Requirement -> Failure
// Mode -> Cause) into a flat row list, one row per Cause, with rowSpan
// counts attached at the row where each merged block starts. A Failure
// Mode with zero causes still gets one row (so it's visible), with the
// cause-level columns left blank.
function flattenTree(processSteps) {
  const rows = [];

  (processSteps || []).forEach((step) => {
    const stepStart = rows.length;

    (step.requirements || []).forEach((req) => {
      const reqStart = rows.length;

      (req.failure_modes || []).forEach((fm) => {
        const fmStart = rows.length;
        const causes = fm.causes && fm.causes.length ? fm.causes : [{}];

        causes.forEach((cause) => {
          rows.push({
            step_name: step.step_name,
            requirement_text: req.requirement_text,
            failure_mode_text: fm.failure_mode_text,
            severity: fm.severity,
            class: fm.class,
            effects: fm.effects || [],
            ...cause,
          });
        });

        rows[fmStart].__fmSpan = rows.length - fmStart;
      });

      if (rows.length > reqStart)
        rows[reqStart].__reqSpan = rows.length - reqStart;
    });

    if (rows.length > stepStart)
      rows[stepStart].__stepSpan = rows.length - stepStart;
  });

  return rows;
}

const th = {
  padding: "8px 10px",
  fontSize: 10,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: 0.3,
  color: "var(--text2)",
  background: "var(--surface2)",
  border: "1px solid var(--border2)",
  whiteSpace: "nowrap",
  position: "sticky",
  top: 0,
};

const td = {
  padding: "8px 10px",
  fontSize: 12,
  border: "1px solid var(--border2)",
  verticalAlign: "top",
  color: "var(--text)",
};

function LineList({ items }) {
  if (!items || items.length === 0)
    return <span style={{ color: "var(--text3)" }}>—</span>;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {items.map((line, i) => (
        <div key={i}>{line}</div>
      ))}
    </div>
  );
}

export default function PfmeaSheetView({ processSteps }) {
  const rows = flattenTree(processSteps);

  if (rows.length === 0) {
    return (
      <div
        className="card"
        style={{ textAlign: "center", color: "var(--text3)", padding: 20 }}
      >
        No process steps
      </div>
    );
  }

  return (
    <div
      style={{
        overflowX: "auto",
        border: "1px solid var(--border2)",
        borderRadius: 8,
      }}
    >
      <table
        style={{ borderCollapse: "collapse", width: "100%", minWidth: 1900 }}
      >
        <thead>
          <tr>
            <th style={{ ...th, minWidth: 160 }} rowSpan={2}>
              Process Steps / Function
            </th>
            <th style={{ ...th, minWidth: 160 }} rowSpan={2}>
              Requirements
            </th>
            <th style={{ ...th, minWidth: 180 }} rowSpan={2}>
              Potential Failure Mode
            </th>
            <th style={{ ...th, minWidth: 160 }} rowSpan={2}>
              Potential Effect(s) of Failure
            </th>
            <th style={{ ...th, minWidth: 60 }} rowSpan={2}>
              Severity
            </th>
            <th style={{ ...th, minWidth: 60 }} rowSpan={2}>
              Class
            </th>
            <th style={{ ...th, minWidth: 180 }} rowSpan={2}>
              Potential cause(s) / Mechanism(s) of Failure
            </th>
            <th style={{ ...th, minWidth: 60 }} rowSpan={2}>
              Occurance
            </th>
            <th style={{ ...th, minWidth: 160 }} rowSpan={2}>
              Current Process controls / Prevention
            </th>
            <th style={{ ...th, minWidth: 160 }} rowSpan={2}>
              Current Process controls - Detection
            </th>
            <th style={{ ...th, minWidth: 60 }} rowSpan={2}>
              Detection
            </th>
            <th style={{ ...th, minWidth: 60 }} rowSpan={2}>
              R.P.N
            </th>
            <th style={{ ...th, minWidth: 180 }} rowSpan={2}>
              Recommended Action(s)
            </th>
            <th style={{ ...th, minWidth: 140 }} rowSpan={2}>
              Responsibility & Target Completion Date
            </th>
            <th style={{ ...th, textAlign: "center" }} colSpan={5}>
              Action Results (after corrective action)
            </th>
          </tr>
          <tr>
            <th style={{ ...th, minWidth: 160, top: 33 }}>Actions Taken</th>
            <th style={{ ...th, minWidth: 60, top: 33 }}>Sev</th>
            <th style={{ ...th, minWidth: 60, top: 33 }}>Occ</th>
            <th style={{ ...th, minWidth: 60, top: 33 }}>Det</th>
            <th style={{ ...th, minWidth: 70, top: 33 }}>R.P.N.</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              {r.__stepSpan && (
                <td
                  style={{
                    ...td,
                    fontWeight: 600,
                    background: "var(--surface)",
                  }}
                  rowSpan={r.__stepSpan}
                >
                  {r.step_name}
                </td>
              )}
              {r.__reqSpan && (
                <td style={td} rowSpan={r.__reqSpan}>
                  {r.requirement_text}
                </td>
              )}
              {r.__fmSpan && (
                <td style={{ ...td, fontWeight: 500 }} rowSpan={r.__fmSpan}>
                  {r.failure_mode_text}
                </td>
              )}
              {r.__fmSpan && (
                <td style={td} rowSpan={r.__fmSpan}>
                  <LineList items={r.effects} />
                </td>
              )}
              {r.__fmSpan && (
                <td
                  style={{ ...td, textAlign: "center", fontWeight: 700 }}
                  rowSpan={r.__fmSpan}
                >
                  {r.severity ?? "—"}
                </td>
              )}
              {r.__fmSpan && (
                <td style={{ ...td, textAlign: "center" }} rowSpan={r.__fmSpan}>
                  {r.class || "—"}
                </td>
              )}

              <td style={td}>{r.cause_text || "—"}</td>
              <td style={{ ...td, textAlign: "center" }}>
                {r.occurrence ?? "—"}
              </td>
              <td style={td}>
                <LineList items={r.control_prevention} />
              </td>
              <td style={td}>
                <LineList items={r.control_detection} />
              </td>
              <td style={{ ...td, textAlign: "center" }}>
                {r.detection ?? "—"}
              </td>
              <td
                style={{
                  ...td,
                  textAlign: "center",
                  fontWeight: 700,
                  color: "var(--accent)",
                }}
              >
                {r.rpn ?? "—"}
              </td>
              <td style={td}>{r.recommended_action || "—"}</td>
              <td style={td}>
                <div>{r.responsibility || "—"}</div>
                <div style={{ color: "var(--text3)", fontSize: 11 }}>
                  {r.target_date || ""}
                </div>
              </td>
              <td style={td}>
                <LineList items={r.action_taken} />
              </td>
              <td style={{ ...td, textAlign: "center" }}>
                {r.revised_severity ?? "—"}
              </td>
              <td style={{ ...td, textAlign: "center" }}>
                {r.revised_occurrence ?? "—"}
              </td>
              <td style={{ ...td, textAlign: "center" }}>
                {r.revised_detection ?? "—"}
              </td>
              <td
                style={{
                  ...td,
                  textAlign: "center",
                  fontWeight: 700,
                  color: "var(--success)",
                }}
              >
                {r.revised_rpn ?? "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
